import {
  Check,
  ChevronDown,
  Sparkles,
  Zap,
  Image as ImageIcon,
} from "lucide-react";
import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import useImageModelStore from "@/store/imageModelStore";

// Provider display configuration
const providerConfig = {
  openai: {
    name: "OpenAI",
    icon: Sparkles,
    color: "text-emerald-600 dark:text-emerald-400",
  },
  qwen: {
    name: "Qwen",
    icon: Zap,
    color: "text-blue-600 dark:text-blue-400",
  },
  flux: {
    name: "Flux",
    icon: ImageIcon,
    color: "text-purple-600 dark:text-purple-400",
  },
  gemini: {
    name: "Google Gemini",
    icon: Sparkles,
    color: "text-orange-600 dark:text-orange-400",
  },
  imagen: {
    name: "Google Imagen",
    icon: ImageIcon,
    color: "text-red-600 dark:text-red-400",
  },
  xai: {
    name: "xAI Grok",
    icon: Sparkles,
    color: "text-gray-600 dark:text-gray-400",
  },
};

const ImageModelSelector = ({ className = "" }) => {
  const {
    selectedModel,
    setSelectedModel,
    getSelectedModel,
    getModelsByProviderGrouped,
  } = useImageModelStore();

  const currentModel = getSelectedModel();
  const groupedModels = getModelsByProviderGrouped();
  const currentProviderConfig =
    providerConfig[currentModel?.provider] || providerConfig.qwen;

  const ProviderIcon = currentProviderConfig.icon;

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Image Model
      </label>

      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 h-auto py-2"
          >
            <div className="flex items-center gap-2 min-w-0">
              <ProviderIcon
                className={`h-4 w-4 flex-shrink-0 ${currentProviderConfig.color}`}
              />
              <div className="flex flex-col items-start min-w-0">
                <span className="font-medium text-sm">
                  {currentModel?.name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {currentProviderConfig.name}
                </span>
              </div>
            </div>
            <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0 ml-2" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-80 p-2 max-h-96 overflow-y-auto"
          align="start"
        >
          <div className="space-y-1">
            {Object.entries(groupedModels).map(([provider, models]) => {
              const config = providerConfig[provider] || providerConfig.qwen;
              const Icon = config.icon;

              return (
                <div key={provider} className="space-y-1">
                  <div className="px-2 py-1.5 flex items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
                    <Icon className={`h-3.5 w-3.5 ${config.color}`} />
                    <span>{config.name}</span>
                  </div>
                  {models.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => setSelectedModel(model.id)}
                      className={`w-full px-2 py-2 text-left rounded-md transition-colors ${
                        selectedModel === model.id
                          ? "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"
                          : "hover:bg-gray-100 dark:hover:bg-gray-700"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">
                              {model.name}
                            </span>
                            {selectedModel === model.id && (
                              <Check className="h-4 w-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                            {model.description}
                          </p>
                          {/* Capability badges */}
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {model.capabilities.editing && (
                              <Badge
                                variant="secondary"
                                className="text-xs px-1.5 py-0"
                              >
                                Edit
                              </Badge>
                            )}
                            {model.capabilities.variations && (
                              <Badge
                                variant="secondary"
                                className="text-xs px-1.5 py-0"
                              >
                                Variations
                              </Badge>
                            )}
                            {model.capabilities.inpainting && (
                              <Badge
                                variant="secondary"
                                className="text-xs px-1.5 py-0"
                              >
                                Inpaint
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {/* Current model info */}
      <div className="text-xs text-gray-500 dark:text-gray-400">
        <p className="line-clamp-2">{currentModel?.description}</p>
      </div>
    </div>
  );
};

ImageModelSelector.propTypes = {
  className: PropTypes.string,
};

export default ImageModelSelector;
